import { useEffect, useMemo, useState } from 'react';
import { Map as IMap } from 'immutable';
import { difference } from 'lodash';

import ModalV2 from '../../shared/ModalV2';
import { RecordUpdateRow } from '../../shared/RecordUpdateRow/RecordUpdateRow';
import { Spoiler } from '../../shared/Spoiler/Spoiler.tsx';
import { Modal } from '../../shared';

import {
    ProfileUpdateStatus,
    useAddProductsToMultipleProfiles,
} from '../../hooks/accounts';

import { extractProductIdsFromPacks } from '../../packs/utils.tsx';
import { toHTMLDate } from '../../utils/dates';
import { generateAndDownloadReport } from '../../utils/download.ts';
import {
    LocalStorageCache,
    StorageCacheKeys,
} from '../../utils/localStorage.ts';

import { UpdateStatus } from '../../types/enums/updateStatus.enum';

import { SpreadsheetProfile } from '../../types/accountTypes';
import { Pack } from '../../types/packTypes';

import styles from './GroupsManagement.module.scss';

const UpdateStatusToText = {
    [UpdateStatus.NotStarted]: 'Not started',
    [UpdateStatus.Updating]: 'Updating',
    [UpdateStatus.Scheduled]: 'Scheduled',
    [UpdateStatus.Updated]: 'Updated',
    [UpdateStatus.Error]: 'Error',
    [UpdateStatus.Invalid]: 'Invalid',
} as const;

const StatusesToPreserve = [
    UpdateStatus.Error,
    UpdateStatus.Updated,
    UpdateStatus.Invalid,
];

type ProfileId = SpreadsheetProfile['profileId'];

const UpdateBatchSize = 100;

type LocalCacheType = {
    packIds: string[];
    statusIndex: [ProfileId, ProfileUpdateStatus][];
};

export const GroupsManagementModal = ({
    title,
    packIdsToAdd,
    allProfiles,
    allPacks,
    onModalCloseClicked,
}: {
    title: string;
    packIdsToAdd: string[];
    allProfiles: SpreadsheetProfile[];
    allPacks: Pack[];
    onModalCloseClicked: () => void;
}) => {
    const oneBatchOnly = allProfiles.length <= UpdateBatchSize;
    const productIds = useMemo(
        () => extractProductIdsFromPacks(packIdsToAdd, allPacks),
        [packIdsToAdd, allPacks],
    );
    const localCache = new LocalStorageCache<LocalCacheType>(
        StorageCacheKeys.GroupManagement,
        (_, b) => b,
    );

    const [isShowAllProfiles, setIsShowAllProfiles] = useState(true);

    const [selectedProfiles, setSelectedProfiles] =
        useState<IMap<ProfileId, number>>(IMap());

    const {
        addToMultipleProfiles,
        setInitialStatusIndex,
        statusIndex: profilesStatusIndex,
        isUpdating: isUpdatingProfiles,
    } = useAddProductsToMultipleProfiles();

    // Select next batch after previous one is done
    useEffect(() => {
        if (isUpdatingProfiles) {
            return;
        }

        selectNext();
    }, [profilesStatusIndex, isUpdatingProfiles]);

    // Save progress to cache
    useEffect(() => {
        const preservedStatuses = profilesStatusIndex.filter(({ status }) =>
            StatusesToPreserve.includes(status),
        );

        if (!preservedStatuses.size) {
            return;
        }

        setSelectedProfiles((prevState) =>
            prevState.deleteAll(preservedStatuses.keys()),
        );

        localCache.save({
            packIds: packIdsToAdd,
            statusIndex: Array.from(preservedStatuses),
        });
    }, [profilesStatusIndex]);

    // Retrieve cache and ask user for decision
    useEffect(() => {
        const cachedData = localCache.getSavedData();

        if (!cachedData) {
            return;
        }

        (async () => {
            const isSamePacks =
                difference(packIdsToAdd, cachedData.packIds).length === 0;
            const isKeepPressed = await askForCachedProfilesStatusDecision(
                cachedData,
                isSamePacks,
            );

            if (isSamePacks && isKeepPressed) {
                // Keep clicked
                setInitialStatusIndex(IMap(cachedData.statusIndex));
                return;
            }

            if (!isSamePacks && !isKeepPressed) {
                // Cancel clicked
                onModalCloseClicked();
                return;
            }

            localCache.clear();
        })();
    }, []);

    const getProfileStatus = (profileId: ProfileId) =>
        profilesStatusIndex.get(profileId) || {
            status: UpdateStatus.NotStarted,
        };

    const selectNext = (amount: number = UpdateBatchSize) => {
        if (!allProfiles?.length) {
            return;
        }

        const nextProfiles: [string, number][] = [];

        for (let i = 0; i < allProfiles.length; i++) {
            const profile = allProfiles[i];
            const { status } = getProfileStatus(profile.profileId);

            if (status === UpdateStatus.NotStarted) {
                nextProfiles.push([profile.profileId, i]);
            }

            if (nextProfiles.length >= amount) {
                break;
            }
        }

        setSelectedProfiles(IMap(nextProfiles));
    };

    const askForCachedProfilesStatusDecision = (
        cachedData: LocalCacheType,
        isSamePacks: boolean,
    ): Promise<boolean> => {
        const statusIndex = IMap(cachedData.statusIndex);

        const attemptedUpdatesResults = statusIndex.reduce(
            (memo: { errors: number; updated: number }, p) => {
                if (p.status === UpdateStatus.Error) {
                    memo.errors++;
                } else if (p.status === UpdateStatus.Updated) {
                    memo.updated++;
                }

                return memo;
            },
            { errors: 0, updated: 0 },
        );

        return Modal.confirm({
            heading: 'Cached data found!',
            text: (
                <div className={styles.cacheDecisionModalText}>
                    There is some cached data from the previous run:
                    <ul>
                        <li>{statusIndex.size} Profiles</li>
                        <li>
                            {cachedData.packIds.length} packs (
                            <label
                                htmlFor={'groupManagementPackIds'}
                                className={styles.showMoreLabel}
                            >
                                show all
                            </label>
                            )
                        </li>
                        <Spoiler id={'groupManagementPackIds'}>
                            {cachedData.packIds.map((packId) => (
                                <div key={packId}>{packId}</div>
                            ))}
                        </Spoiler>
                        <li>
                            {attemptedUpdatesResults.updated} updated /{' '}
                            {attemptedUpdatesResults.errors} errors
                        </li>
                    </ul>
                    {isSamePacks ? (
                        <p className={styles.warningKeep}>
                            It is the same packs as last time - you can continue
                            where you left off by keeping the cache!
                        </p>
                    ) : (
                        <p className={styles.warningClear}>
                            It is different packs this time - you must clear
                            cache to continue!
                        </p>
                    )}
                </div>
            ),
            cancelLabel: isSamePacks ? 'Clear cache' : 'Cancel',
            okLabel: isSamePacks ? 'Keep cache' : 'Clear cache',
        });
    };

    const toggleProfileSelection = (profileId: string) => {
        if (selectedProfiles.has(profileId)) {
            setSelectedProfiles(selectedProfiles.delete(profileId));
        } else {
            const index = allProfiles.findIndex(
                (p) => p.profileId === profileId,
            );
            setSelectedProfiles(selectedProfiles.set(profileId, index));
        }
    };

    const onSelectNextClicked = () => {
        selectNext();
    };

    const onDeselectAllClicked = () => {
        setSelectedProfiles(selectedProfiles.clear());
    };

    const onModalUpdateConfirmClicked = () => {
        if (isUpdatingProfiles) {
            return;
        }

        addToMultipleProfiles(
            Array.from(selectedProfiles.sort().keys()),
            productIds,
        );
    };

    const onGetReportClicked = () => {
        const dataToExport = allProfiles.map((profile) => {
            const { status, error } = getProfileStatus(profile.profileId);

            return {
                ...profile,
                status: UpdateStatusToText[status],
                error,
            };
        });

        const selectedPackIds = packIdsToAdd.join('-');
        const currentDate = toHTMLDate(new Date());
        const filename = `[${currentDate}]_${selectedPackIds}_update-log.csv`;

        generateAndDownloadReport<
            SpreadsheetProfile & {
                status: (typeof UpdateStatusToText)[keyof typeof UpdateStatusToText];
                error: ProfileUpdateStatus['error'];
            }
        >(dataToExport, filename);
    };

    const hasPerformedUpdates =
        !isUpdatingProfiles &&
        profilesStatusIndex.some(({ status }) =>
            StatusesToPreserve.includes(status),
        );

    const updatedCount = allProfiles.filter(
        (p) => getProfileStatus(p.profileId).status === UpdateStatus.Updated,
    ).length;

    return (
        <ModalV2>
            <div className={styles.modalHeader}>
                <h1>{title}</h1>
                <fieldset>
                    <legend>Status</legend>
                    <input
                        checked={isShowAllProfiles}
                        id={`showAll`}
                        name={'showAll'}
                        type="radio"
                        onChange={() => setIsShowAllProfiles(true)}
                    />
                    <label htmlFor="showAll">Show all</label>
                    <input
                        checked={!isShowAllProfiles}
                        id={`hideUpdated`}
                        name={'hideUpdated'}
                        type="radio"
                        onChange={() => setIsShowAllProfiles(false)}
                    />
                    <label htmlFor="hideUpdated">Hide updated</label>
                </fieldset>
            </div>
            <header className={styles.header}>
                <div>{`${updatedCount} updated / ${selectedProfiles.size} selected / ${allProfiles.length} total`}</div>
                <div className={styles.selectButtons}>
                    <button
                        type="button"
                        disabled={isUpdatingProfiles}
                        onClick={onDeselectAllClicked}
                    >
                        Deselect all
                    </button>

                    <button
                        type="button"
                        disabled={isUpdatingProfiles}
                        onClick={onSelectNextClicked}
                    >
                        {oneBatchOnly
                            ? 'Select all'
                            : `Select next ${UpdateBatchSize}`}
                    </button>
                </div>
            </header>
            <div className={styles.modalContent}>
                {allProfiles.map((p) => {
                    const { status, error } = getProfileStatus(p.profileId);

                    if (!isShowAllProfiles && status === UpdateStatus.Updated) {
                        return null;
                    }

                    return (
                        <RecordUpdateRow
                            key={p.profileId}
                            id={p.profileId}
                            text={`${p.profileId} (${p.name})`}
                            status={status}
                            error={error}
                            isSelected={selectedProfiles.has(p.profileId)}
                            isDisabled={isUpdatingProfiles}
                            onCheckboxClick={toggleProfileSelection}
                        />
                    );
                })}
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginTop: '1em',
                }}
            >
                <div>
                    <button
                        className="secondary"
                        type="button"
                        disabled={!hasPerformedUpdates}
                        onClick={onGetReportClicked}
                    >
                        Get update log
                    </button>
                </div>
                <div className={styles.buttons}>
                    <button
                        className="danger"
                        type="button"
                        disabled={isUpdatingProfiles}
                        onClick={onModalCloseClicked}
                    >
                        Close
                    </button>
                    <button
                        className="primary"
                        type="button"
                        disabled={
                            isUpdatingProfiles || selectedProfiles.size === 0
                        }
                        onClick={onModalUpdateConfirmClicked}
                    >
                        {hasPerformedUpdates ? 'Update next' : 'Update'}
                    </button>
                </div>
            </div>
        </ModalV2>
    );
};
